import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import CalculadoraUber from '../CalculadoraUber';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'calculadora-lucro-uber';

/** Página /ferramentas/calculadora-lucro-uber */
const UberPage: React.FC = () => (
  <>
    <Helmet>
      <title>Calculadora de Lucro Uber / 99: Ganho Líquido Real por Dia – Gas2</title>
      <meta
        name="description"
        content="Calcule seu lucro líquido real como motorista de app: desconte combustível, depreciação e gastos extras dos ganhos brutos. Veja o custo real por km rodado. Grátis."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Calculadora Lucro Uber / 99: Ganho Líquido Real – Gas2" />
      <meta
        property="og:description"
        content="Calcule seu lucro líquido real como motorista de app descontando combustível, depreciação e gastos extras."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Calculadora Lucro Uber / 99 – Gas2" />
      <meta
        name="twitter:description"
        content="Descubra seu lucro real como motorista de app descontando todos os custos operacionais."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Motorista de App
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Calculadora de Lucro Líquido Uber / 99
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Descubra quanto você{' '}
            <strong className="text-slate-200">realmente ganhou</strong> no dia depois de
            descontar combustível, depreciação do veículo e gastos extras. O aplicativo salva
            o consumo e a depreciação do seu carro automaticamente.
          </p>
        </motion.header>

        {/* ── Calculadora ── */}
        <CalculadoraUber />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Por Que Calcular o Lucro Líquido Real?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Muitos motoristas de aplicativo acompanham apenas os ganhos brutos exibidos
              no painel do app — e se surpreendem ao perceber que parte significativa desse
              valor vai para o combustível, manutenção e desgaste do veículo. A Uber/99
              retém entre 20 e 25% do valor de cada corrida, e os custos operacionais podem
              consumir outros 30 a 40% dos ganhos brutos, dependendo do carro e da região.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              O Que é Depreciação do Veículo?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Depreciação é a perda de valor do veículo com o tempo e o uso. Para motoristas
              de app, isso é acelerado: cada quilômetro reduz o valor de revenda do carro.
              Além disso, maiores quilometragens implicam trocas mais frequentes de óleo,
              filtros, pneus, pastilhas de freio e correias. Estimar{' '}
              <strong className="text-slate-200">R$ 0,25/km</strong> é um valor conservador
              que cobre manutenção básica — carros mais antigos podem custar R$ 0,35–0,45/km.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Quanto Custa Trabalhar Como Motorista de App?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Considere um dia típico com R$ 250 brutos, 180 km rodados e gasolina a R$ 5,89/l
              com consumo de 12 km/l:
            </p>
            <div className="mt-4 rounded-xl bg-slate-900 border border-slate-800 p-5 space-y-2">
              {[
                { item: 'Ganhos brutos', valor: 'R$ 250,00', dir: '' },
                { item: 'Combustível (15 l × R$ 5,89)', valor: '− R$ 88,35', dir: 'text-red-400' },
                { item: 'Depreciação (180 km × R$ 0,25)', valor: '− R$ 45,00', dir: 'text-red-400' },
                { item: 'Gastos extras (alimentação, pedágio)', valor: '− R$ 25,00', dir: 'text-red-400' },
              ].map(row => (
                <div key={row.item} className="flex justify-between text-sm">
                  <span className="text-slate-400">{row.item}</span>
                  <span className={`font-medium text-white ${row.dir}`}>{row.valor}</span>
                </div>
              ))}
              <div className="border-t border-slate-700 pt-2 flex justify-between text-sm font-bold">
                <span className="text-white">Lucro Líquido</span>
                <span className="text-emerald-400">R$ 91,65</span>
              </div>
            </div>
            <p className="text-slate-500 text-xs mt-2">
              Exemplo ilustrativo. Use a calculadora acima com os dados reais do seu dia.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes — Motorista de App
            </h2>
            <div className="mt-3 space-y-4">
              {[
                {
                  q: 'A Uber desconta imposto dos ganhos?',
                  a: 'A Uber retém a comissão da plataforma (20–25%), mas não desconta imposto de renda. O motorista como autônomo é responsável pelo recolhimento do IR e do INSS, se aplicável.',
                },
                {
                  q: 'Vale mais a pena trabalhar de manhã ou à noite?',
                  a: 'Depende da cidade. Em geral, horários de pico (7h–9h e 17h–20h) e madrugadas de sexta/sábado têm tarifa dinâmica mais alta. Use o histórico do app para comparar a receita por hora.',
                },
                {
                  q: 'Como reduzir o custo por km rodado?',
                  a: 'Manter pneus calibrados, motor afinado, câmbio suave e evitar frenagens bruscas pode reduzir o consumo em até 15%. Registre cada abastecimento no Gas2 para acompanhar a evolução.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default UberPage;

